// src/components/digital_twin/SimulationPanel.js
// --- UPDATE: v2.0 — fully connected to the interdependency simulation engine
//   • 7 scenarios: pipe burst, pump failure, power outage, contamination
//     (network) + hurricane, live flood forecast, storm-surge flooding (weather/AI)
//   • UI parameters are mapped to the engine's real parameters (simulationScenarios.js).
//     v1 posted keys like `severity` the engine never read, so every run used defaults.
//   • Calls POST /simulations/run and RENDERS the result (v1 discarded the response
//     and only showed the parameters it had sent).
//   • Running state with elapsed time, ensemble progress (WebSocket) and cancel.
//   • Results: KPIs, damage, restoration plan, ensemble, playback, at-risk list,
//     export — and the result is shared with the page so the 3D scene reacts.
//   • Scenario list filtered by the selected asset's type, grouped, with AI badges.
//   • Backward compatible: works stand-alone (own state) or with a `simulation`
//     object from useSimulation() passed by the page; still calls onRunSimulation(title|null).
// --- END UPDATE
import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import {
    Box, Heading, VStack, Button, Text, useToast,
    useColorModeValue, Flex, Icon, HStack, Badge, Divider,
    Slider, SliderTrack, SliderFilledTrack, SliderThumb,
    Select, Collapse, SimpleGrid,
    // --- UPDATE
    Switch, Progress, Tooltip, Alert, AlertIcon, AlertDescription,
    // --- END UPDATE
} from '@chakra-ui/react';
// (scenario icons now live in simulationScenarios.js)
import {
    FaFlask, FaPlay, FaStop,
    FaExclamationTriangle, FaSlidersH,
    // --- UPDATE
    FaBrain,
    // --- END UPDATE
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
// --- UPDATE
import useSimulation from '../../hooks/useSimulation';
import {
    SCENARIOS, SCENARIO_GROUPS, defaultValuesFor, formatParam,
} from './simulationScenarios';
import SimulationResultsView from './SimulationResultsView';
import SimulationRunHistory from './SimulationRunHistory'; // --- UPDATE (twin v8)
// --- END UPDATE

const MotionBox = motion(Box);

// ==================== SCENARIO CARD COMPONENT ====================
const ScenarioCard = memo(({ sc, isSelected, onClick, disabledReason }) => {
    const hoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');
    const cardBg = useColorModeValue('gray.50', 'whiteAlpha.50');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const mutedText = useColorModeValue('gray.500', 'gray.400');

    const selectedBg = useColorModeValue(`${sc.color}.50`, `rgba(255,255,255,0.05)`);
    const iconBg = useColorModeValue(`${sc.color}.100`, `${sc.color}.900`);
    const titleColor = useColorModeValue('gray.800', 'white');

    return (
        <Box
            as="button"
            type="button"
            w="100%"
            textAlign="left"
            onClick={onClick}
            p={3}
            borderRadius="lg"
            borderWidth="2px"
            borderColor={isSelected ? `${sc.color}.400` : borderColor}
            bg={isSelected ? selectedBg : cardBg}
            _hover={{ bg: isSelected ? undefined : hoverBg }}
            transition="all 0.2s"
            position="relative"
            overflow="hidden"
            // --- UPDATE: accessibility + disabled hint
            aria-pressed={isSelected}
            title={disabledReason || undefined}
            // --- END UPDATE
        >
            <HStack spacing={3} align="flex-start">
                <Flex p={2} bg={iconBg} borderRadius="md" flexShrink={0}>
                    <Icon as={sc.icon} color={`${sc.color}.500`} boxSize={4} />
                </Flex>
                <VStack align="start" spacing={0.5} flex={1} minW={0}>
                    {/* --- UPDATE: AI badge for weather/ML scenarios */}
                    <HStack spacing={1.5}>
                        <Text fontSize="sm" fontWeight="700" color={titleColor} fontFamily="sans-serif">
                            {sc.title}
                        </Text>
                        {sc.ai && <Badge colorScheme="purple" fontSize="8px" px={1.5}><Icon as={FaBrain} boxSize={2} mr={0.5} />AI</Badge>}
                    </HStack>
                    {/* --- END UPDATE */}
                    <Text fontSize="11px" color={mutedText} lineHeight="short" fontFamily="sans-serif">
                        {sc.desc}
                    </Text>
                    {/* --- UPDATE */}
                    {disabledReason && <Text fontSize="10px" color="orange.400">{disabledReason}</Text>}
                    {/* --- END UPDATE */}
                </VStack>
            </HStack>
        </Box>
    );
});
ScenarioCard.displayName = 'ScenarioCard';

// --- UPDATE: parameter control
const ParamControl = memo(({ param, value, onChange, trackBg, mutedText }) => (
    <Box>
        <Flex justify="space-between" mb={2} align="center">
            <HStack spacing={1}>
                <Text fontSize="11px" fontWeight="600" color={mutedText}>{param.label}</Text>
                {param.ai && <Icon as={FaBrain} boxSize={2.5} color="purple.400" />}
            </HStack>
            {param.type !== 'switch' && (
                <Text fontSize="11px" fontWeight="800" fontFamily="mono" color="purple.500">
                    {formatParam(param, value)}
                </Text>
            )}
            {param.type === 'switch' && (
                <Switch size="sm" colorScheme="purple" isChecked={!!value} onChange={(e) => onChange(param.id, e.target.checked)} aria-label={param.label} />
            )}
        </Flex>

        {param.type === 'slider' && (
            <Slider
                min={param.min}
                max={param.max}
                step={param.step || 1}
                value={value}
                onChange={(val) => onChange(param.id, val)}
                colorScheme="purple"
                aria-label={param.label}
            >
                <SliderTrack bg={trackBg}><SliderFilledTrack /></SliderTrack>
                <SliderThumb boxSize={4} />
            </Slider>
        )}

        {param.type === 'select' && (
            <Select
                size="sm"
                borderRadius="md"
                fontSize="11px"
                value={value}
                onChange={(e) => onChange(param.id, e.target.value)}
                aria-label={param.label}
            >
                {param.options.map(opt => (
                    <option key={opt} value={opt}>{param.labels?.[opt] || opt}</option>
                ))}
            </Select>
        )}
    </Box>
));
ParamControl.displayName = 'ParamControl';
// --- END UPDATE

// ==================== MAIN COMPONENT ====================
const SimulationPanel = ({
    assetId,
    assetType = 'unknown',
    onRunSimulation,
    // --- UPDATE: page integration (all optional)
    simulation: externalSimulation,
    selectedAsset = null,
    centroid = null,
    onFocusAsset,
    // --- END UPDATE
}) => {
    const toast = useToast();
    const [selectedScenarioId, setSelectedScenarioId] = useState('');
    const [scenarioParams, setScenarioParams] = useState({});

    // --- UPDATE: use the page's simulation state when provided, otherwise our own
    const internalSimulation = useSimulation();
    const sim = externalSimulation || internalSimulation;
    const { isRunning, result, meta, error, progress, elapsedMs } = sim;
    // --- END UPDATE

    // Theme Hooks
    const bg = useColorModeValue('transparent', 'transparent');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const mutedText = useColorModeValue('gray.500', 'gray.400');
    const paramBg = useColorModeValue('white', 'gray.800');
    const sliderTrackBg = useColorModeValue('gray.200', 'gray.600');

    const activeBannerBg = useColorModeValue('orange.100', 'rgba(221, 107, 32, 0.2)');
    const activeBannerBorder = useColorModeValue('orange.300', 'orange.600');
    const activeBannerText = useColorModeValue('orange.800', 'orange.200');

    // Filter Scenarios based on Asset Context
    // --- UPDATE: weather scenarios ('unknown') are always available; asset-origin scenarios
    // are listed for any asset but explain when the selected asset is an unusual origin.
    const normalizedType = String(assetType || selectedAsset?.type || selectedAsset?.asset_type || 'unknown').toLowerCase();
    const availableScenarios = useMemo(() => SCENARIOS.filter(sc =>
        sc.targetTypes.includes('unknown') || sc.targetTypes.includes(normalizedType)
    ), [normalizedType]);
    const selectedScenario = availableScenarios.find(sc => sc.id === selectedScenarioId);
    // --- END UPDATE

    // Initialize default parameters when a scenario is selected
    useEffect(() => {
        if (selectedScenario) {
            setScenarioParams(defaultValuesFor(selectedScenario));
        }
    }, [selectedScenarioId, selectedScenario]);

    const handleParamChange = useCallback((paramId, value) => {
        setScenarioParams(prev => ({ ...prev, [paramId]: value }));
    }, []);

    // --- UPDATE: surface errors from the run
    useEffect(() => {
        if (!error) return;
        toast({
            title: error.status === 429 ? 'Simulation busy' : 'Simulation Error',
            description: error.message,
            status: error.status === 429 ? 'warning' : 'error',
            duration: 6000,
            isClosable: true,
        });
    }, [error, toast]);
    // --- END UPDATE

    const handleRun = async () => {
        if (!selectedScenario) return;

        // --- UPDATE: real engine call through the shared simulation state
        const body = await sim.run(selectedScenario, scenarioParams, {
            assetId: assetId ?? null,
            selectedAsset,
            centroid,
        });
        if (!body) return;

        if (onRunSimulation) onRunSimulation(selectedScenario.title);

        const s = body.data?.summary || {};
        toast({
            title: `Simulated: ${selectedScenario.title}`,
            description: s.moderateOrWorse != null
                ? `${s.moderateOrWorse} assets with moderate+ damage, ${s.assetsFlooded ?? 0} flooded.`
                : `${s.totalAffected ?? 0} assets affected (${s.criticalCount ?? 0} critical).`,
            status: 'warning',
            duration: 5000,
            isClosable: true,
        });
        // --- END UPDATE
    };

    const handleStop = () => {
        // --- UPDATE
        sim.clear();
        // --- END UPDATE
        setSelectedScenarioId('');
        if (onRunSimulation) onRunSimulation(null);

        toast({
            title: "Simulation Stopped",
            description: "Digital Twin returning to live telemetry.",
            status: "info",
            duration: 3000,
            isClosable: true
        });
    };

    // --- UPDATE: grouped scenario list
    const grouped = useMemo(() => SCENARIO_GROUPS.map(g => ({
        ...g, items: availableScenarios.filter(s => s.group === g.id),
    })).filter(g => g.items.length), [availableScenarios]);

    const needsAssetHint = (sc) => (sc.requiresAsset && assetId == null
        ? 'No asset selected — runs from the first network node'
        : null);
    // --- END UPDATE

    return (
        <Box w="100%" bg={bg} p={0}>
            <HStack spacing={2} mb={1}>
                <Icon as={FaFlask} color="purple.500" />
                <Heading size="xs" textTransform="uppercase" letterSpacing="0.06em">Scenario Simulator</Heading>
            </HStack>
            <Text fontSize="11px" color={mutedText} mb={4} lineHeight="short" fontFamily="sans-serif">
                Run parametric "what-if" scenarios — network failures or hurricanes and flooding — and see
                damage, cascading impact and recovery on the twin.
            </Text>

            <AnimatePresence mode="wait">
                {/* --- UPDATE: running state */}
                {isRunning ? (
                    <MotionBox key="running" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <Box p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor} bg={paramBg} mb={3}>
                            <HStack justify="space-between" mb={2}>
                                <Text fontSize="sm" fontWeight="800">Running {selectedScenario?.title || 'simulation'}…</Text>
                                <Text fontSize="xs" fontFamily="mono" color={mutedText}>{(elapsedMs / 1000).toFixed(1)}s</Text>
                            </HStack>
                            <Progress size="sm" colorScheme="purple" borderRadius="full"
                                isIndeterminate={progress == null} value={progress ?? undefined} hasStripe isAnimated />
                            <Text fontSize="10px" color={mutedText} mt={2}>
                                {progress != null ? `Ensemble ${progress}% complete` : 'Computing hazards, damage and cascading impact…'}
                            </Text>
                        </Box>
                        <Button w="100%" size="sm" variant="outline" onClick={sim.cancel}>Cancel</Button>
                    </MotionBox>
                ) : result ? (
                // --- END UPDATE
                    <MotionBox
                        key="active-state"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                    >
                        <Box bg={activeBannerBg} p={3} borderRadius="lg" borderWidth="1px" borderColor={activeBannerBorder} mb={3} textAlign="center">
                            <HStack justify="center" spacing={2}>
                                <Icon as={FaExclamationTriangle} color={activeBannerText} boxSize={4} />
                                <Text fontWeight="800" color={activeBannerText} fontSize="sm" textTransform="uppercase">
                                    {meta?.title || 'Simulation'} active
                                </Text>
                            </HStack>

                            {/* Display the active parameters being simulated */}
                            {meta?.values && SCENARIOS.find(s => s.id === meta.scenario) && (
                                <SimpleGrid columns={2} spacing={2} mt={2}>
                                    {SCENARIOS.find(s => s.id === meta.scenario).params.map((p) => (
                                        <Box key={p.id} bg="rgba(0,0,0,0.08)" p={1.5} borderRadius="md">
                                            <Text fontSize="8px" textTransform="uppercase" color={activeBannerText} opacity={0.8}>{p.label}</Text>
                                            <Text fontSize="11px" fontWeight="700" color={activeBannerText} fontFamily="mono">{formatParam(p, meta.values[p.id])}</Text>
                                        </Box>
                                    ))}
                                </SimpleGrid>
                            )}

                            <Text fontSize="10px" color={activeBannerText} opacity={0.8} fontFamily="sans-serif" mt={2}>
                                Scene shows simulated impact — live telemetry overlay is paused.
                            </Text>
                        </Box>

                        {/* --- UPDATE: real results */}
                        <SimulationResultsView sim={sim} onFocusAsset={onFocusAsset} />
                        {/* --- END UPDATE */}

                        <Button colorScheme="red" w="100%" mt={3} leftIcon={<FaStop />} onClick={handleStop} fontWeight="700" fontFamily="sans-serif" fontSize="12px" letterSpacing="0.05em">
                            STOP SIMULATION
                        </Button>
                    </MotionBox>
                ) : (
                    <MotionBox
                        key="selection-state"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        {/* --- UPDATE: grouped list */}
                        <VStack spacing={4} align="stretch" mb={4}>
                            {grouped.map(g => (
                                <Box key={g.id}>
                                    <HStack spacing={1.5} mb={2}>
                                        <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color={mutedText}>{g.label}</Text>
                                        {g.ai && <Badge colorScheme="purple" fontSize="8px">AI / ML</Badge>}
                                    </HStack>
                                    <VStack spacing={2} align="stretch">
                                        {g.items.map((sc) => (
                                            <ScenarioCard
                                                key={sc.id}
                                                sc={sc}
                                                isSelected={selectedScenarioId === sc.id}
                                                onClick={() => setSelectedScenarioId(sc.id)}
                                                disabledReason={selectedScenarioId === sc.id ? needsAssetHint(sc) : null}
                                            />
                                        ))}
                                    </VStack>
                                </Box>
                            ))}
                        </VStack>
                        {/* --- END UPDATE */}

                        {/* --- NEW: DYNAMIC PARAMETER TUNING UI --- */}
                        <Collapse in={!!selectedScenarioId} animateOpacity>
                            <Box bg={paramBg} p={4} borderRadius="xl" borderWidth="1px" borderColor={borderColor} mb={4}>
                                <HStack spacing={2} mb={4}>
                                    <Icon as={FaSlidersH} color="purple.500" />
                                    <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color="purple.500" letterSpacing="0.05em">
                                        Tune Parameters
                                    </Text>
                                </HStack>

                                <VStack spacing={5} align="stretch">
                                    {selectedScenario?.params.map(param => (
                                        <ParamControl
                                            key={param.id}
                                            param={param}
                                            value={scenarioParams[param.id] ?? param.default}
                                            onChange={handleParamChange}
                                            trackBg={sliderTrackBg}
                                            mutedText={mutedText}
                                        />
                                    ))}
                                </VStack>

                                {/* --- UPDATE: scope hint */}
                                {selectedScenario?.id === 'hurricane' && scenarioParams.ensemble && scenarioParams.ensemble !== 'Off' && (
                                    <Alert status="info" mt={4} borderRadius="md" py={2}>
                                        <AlertIcon boxSize={3} />
                                        <AlertDescription fontSize="10px">
                                            Ensemble runs perturb track, intensity and rainfall to give P10/P50/P90 outcomes — expect a few seconds per 25 storms.
                                        </AlertDescription>
                                    </Alert>
                                )}
                                {/* --- END UPDATE */}
                            </Box>
                        </Collapse>

                        <Divider borderColor={borderColor} mb={4} />

                        <Tooltip label={!selectedScenarioId ? 'Pick a scenario first' : ''} hasArrow isDisabled={!!selectedScenarioId}>
                            <Button
                                colorScheme="purple"
                                w="100%"
                                leftIcon={<FaPlay />}
                                onClick={handleRun}
                                isLoading={isRunning}
                                isDisabled={!selectedScenarioId}
                                fontWeight="700"
                                fontFamily="sans-serif"
                                fontSize="12px"
                                letterSpacing="0.05em"
                            >
                                EXECUTE SCENARIO
                            </Button>
                        </Tooltip>
                        {/* --- UPDATE (twin v8): re-open a stored run (yours or another operator's) */}
                        <Box mt={5}><SimulationRunHistory onOpen={sim.loadRun} refreshKey={meta?.run_id} activeRunId={meta?.run_id} /></Box>
                        {/* --- END UPDATE */}
                    </MotionBox>
                )}
            </AnimatePresence>
        </Box>
    );
};

export default SimulationPanel;